import React, {useState} from 'react';
import { statuses, useScheduleSuggestion } from '../../../../Data/Jobs';
import { classNames, dateToString, getEntityLink, weeksInYear, weekString } from '../../../../Services/helpers';
import Button from '../../../../Components/Button/Button';
import Dropdown from '../../../../Components/Dropdown';
import Icon from '../../../../Components/Icons/Icon';
import Loading, { PageLoading } from '../../../../Components/Loading';
import Money from '../../../../Components/Formatting/Money';
import RelativeWeek from '../../../../Components/Formatting/RelativeWeek';
import Schedule from './Schedule';
import DateSquare from './DateSquare';
import Compact from './Compact';
import JobDot from './JobDot';
import UserName from '../../../../Components/Formatting/UserName';
import { Link } from 'react-router-dom';

const SuggestedDates = () => {
    const thisWeek = weekString();
    const [week, setWeek] = useState(weekString());
    // Don't include the week string for the current week, so we can preload it
    const {dates, isLoading, refresh} = useScheduleSuggestion(week === thisWeek ? null : week, true);
    const [scheduling, setScheduling] = useState([]);
    const [expanded, setExpanded] = useState(false);

    const prevWeek = () => {
        let [y, w] = week.split('-W').map(Number);
        w--;
        if (w < 1) {
            y--;
            w = 52;
        }
        setWeek(`${y}-W${w}`);
    }
    const nextWeek = () => {
        let [y, w] = week.split('-W').map(Number);
        w++;
        if (w > weeksInYear(y)) {
            y++;
            w = 1;
        }
        setWeek(`${y}-W${w}`);
    }

    const today = dateToString(new Date());

    return <div>
        <div className="flex flex-row justify-between items-center mb-2">
            <h3 className="text-lg font-bold text-brand grow shrink-0">
                Week overview
                {week !== thisWeek && <span className="ml-2 font-normal text-muted hidden md:inline-block">
                    (<RelativeWeek week={week} />)
                </span>}
                {isLoading && <Loading className="ml-2" />}
            </h3>
            <div className="flex flex-row gap-x-5">
                <Icon icon="LeftChevron" onClick={prevWeek} />
                <Icon icon="RightChevron" onClick={nextWeek} />
                <Icon icon={expanded ? 'ArrowsDiagonalIn' : 'ArrowsDiagonalOut'}
                    onClick={() => setExpanded(!expanded)}
                />
            </div>
        </div>

        {dates && <>
            <Inner {...{expanded, setExpanded, today, isLoading, setScheduling, dates}} />
            <Schedule isOpen={scheduling.length === 2}
                setIsOpen={() => setScheduling([])}
                date={scheduling[0] ?? null}
                assignTo={scheduling[1] ?? null}
                rounds={dates[scheduling[0] ?? 'null']?.[scheduling[1] ?? 'null']?.suggested?.rounds ?? null}
                jobs={dates[scheduling[0] ?? 'null']?.[scheduling[1] ?? 'null']?.suggested?.jobs ?? null}
                onScheduled={() => refresh()}
            />
        </>}
        {!dates && <PageLoading />}
    </div>
}

const Inner = ({expanded, setExpanded, today, isLoading, setScheduling, dates}) => {
    if (!expanded) {
        return <Compact {...{today, dates, setExpanded}} />
    }

    return <div className="flex flex-col">
        {Object.keys(dates).map(date => {
            return <Row key={date} {...{today, date, isLoading, setScheduling, suggestion: dates[date]}} />
        })}
    </div>
}

const Row = ({today, date, suggestion, isLoading, setScheduling}) => {

    const noScheduledJobs = Object.values(suggestion).every(user => user.scheduled.jobs.length === 0) &&
                            Object.values(suggestion).every(user => user.scheduled.rounds.every(round => round.jobs.length === 0));

    const noSuggestedJobs = Object.values(suggestion).every(user => user.suggested.jobs.length === 0) &&
                            Object.values(suggestion).every(user => user.suggested.rounds.every(round => round.jobs.length === 0));

    return <div className={classNames(
            'w-full pb-3 mb-3 border-b last:border-none flex flex-row gap-x-3 justify-between cursor',
            (noScheduledJobs && noSuggestedJobs) ? 'items-center' : 'items-top'
        )}>
        <div className="shrink-0 self-start">
            <DateSquare {...{date, today}} />
        </div>
        <div className="grow text-sm">

            {(noScheduledJobs && noSuggestedJobs) && <p className="text-muted">
                No jobs to show
            </p>}

            {Object.keys(suggestion).map(userId => {
                const userSuggestion = suggestion[userId];
                const unassigned = userId === 'unassigned';
                const hasScheduledJobs = userSuggestion.scheduled.jobs.length > 0 || userSuggestion.scheduled.rounds.some(round => round.jobs.length > 0);
                const hasSuggestedJobs = userSuggestion.suggested.jobs.length > 0 || userSuggestion.suggested.rounds.some(round => round.jobs.length > 0);

                if (!hasScheduledJobs && !hasSuggestedJobs) {
                    return null;
                }

                return <div className="mb-4 last:mb-0">
                    <p className="mb-2 font-bold">
                        {unassigned ? 'Job with no assigned user' : <>Jobs for <UserName userId={userId} you="you" /></>}
                    </p>
                    {!hasScheduledJobs && <p className="text-muted">No scheduled jobs</p>}
                    <RoundsAndJobs rounds={userSuggestion.scheduled.rounds} jobs={userSuggestion.scheduled.jobs} />

                    {hasSuggestedJobs && <div
                            className="mt-2 p-2 surface-2 border border-slate-200 dark:border-slate-800 rounded-lg flex flex-row justify-between items-center"
                        >
                        <div>
                            <p className="mb-1 font-bold">
                                {!unassigned && <>
                                    Suggested jobs for <UserName userId={userId} you="you" /> on {new Date(date).toLocaleDateString('en-GB', {weekday: 'long'})}
                                </>}
                                {unassigned && <>
                                    Suggested jobs for {new Date(date).toLocaleDateString('en-GB', {weekday: 'long'})}
                                </>}
                            </p>

                            <RoundsAndJobs rounds={userSuggestion.suggested.rounds} jobs={userSuggestion.suggested.jobs} />
                        </div>
                        <div>
                            <Button theme="primary"
                                label="Schedule"
                                disabled={isLoading}
                                onClick={() => setScheduling([date, userId])}
                                zeroMargin
                            />
                        </div>
                    </div>}
                </div>
            })}

        </div>
        <div className="shrink-0">
            <Dropdown
                disabled={isLoading}
                options={[
                    {
                        icon: 'RightChevron',
                        title: 'View suggested jobs',
                        to: `/plan/jobs?suggestedDate=${date}`
                    }
                ]}
            />
        </div>
    </div>
}

const RoundsAndJobs = ({rounds, jobs}) => {
    return <>
        {rounds.map(round => <JobsRow key={round.id}
            title={round.name}
            jobs={round.jobs}
            link={getEntityLink('Rounds/Round', round.id)}
        />)}

        {jobs.length > 0 && <JobsRow
            title="Jobs without rounds"
            jobs={jobs}
        />}
    </>
}

const JobsRow = ({title, jobs, link}) => {

    const totalValue = jobs.reduce(
        (agg, job) => statuses?.[job.status]?.excludeFromTotals ? agg : agg + job.cost,
        0
    );

    let theTitle = title;
    if (link) {
        theTitle = <Link to={link} className="subtle-link">{title}</Link>
    }

    return <div>
        <p>{theTitle} <span className="text-muted"> &nbsp; - &nbsp; <Money pence={totalValue} /></span></p>
        <div className="flex flex-row flex-wrap gap-1 my-1 md:max-w-[90%]">
            {jobs.map(job => <JobDot key={job.id} job={job} />)}
        </div>
    </div>
}


export default SuggestedDates;
